import React from 'react';
import { useMatch } from 'react-router-dom';
import {
    Card,
    CardActionArea,
    CardContent,
    Grid,
    Skeleton,
    styled,
    Tooltip,
    TooltipProps,
    Typography,
} from '@mui/material';

import { useAppSelector } from '../../state/hooks';
import { ApiStatus, DayOfWeek, ValueReferences } from '../../utils/enums';
import { formatBalance, formatNumber } from '../../utils/formattingUtils';
import { privateRoutes } from '../../routes/appRouteList';

const classesPrefix = 'areaBalance';

const classes = {
    goodBalance: `${classesPrefix}-goodBalance`,
    okayBalance: `${classesPrefix}-okayBalance`,
    poorBalance: `${classesPrefix}-poorBalance`,
    dayOfWeekGridItem: `${classesPrefix}-dayOfWeekGridItem`,
    selectedCard: `${classesPrefix}-selectedCard`,
    dayOfWeekCardContent: `${classesPrefix}-dayOfWeekCardContent`,
    dayOfWeekBalance: `${classesPrefix}-dayOfWeekBalance`,
    totalOrders: `${classesPrefix}-totalOrders`,
    areaNameLabel: `${classesPrefix}-areaNameLabel`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`& .${classes.goodBalance}`]: {
            color: theme.palette.success.main,
        },
        [`& .${classes.okayBalance}`]: {
            color: theme.palette.warning.dark,
        },
        [`& .${classes.poorBalance}`]: {
            color: theme.palette.error.main,
        },
        [`& .${classes.selectedCard}`]: {
            backgroundColor: theme.palette.action.selected,
        },
        [`& .${classes.dayOfWeekGridItem}`]: {
            width: '14.28%',
            [theme.breakpoints.down('sm')]: {
                width: '25%',
            },
        },
        [`& .${classes.dayOfWeekCardContent}`]: {
            textAlign: 'center',
            padding: '8px',
        },
        [`& .${classes.dayOfWeekBalance}`]: {
            fontWeight: 700,
        },
    };
});

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.totalOrders}`]: {
            display: 'flex',
            paddingRight: '8px',
        },
        [`& .${classes.areaNameLabel}`]: {
            fontWeight: 600,
            paddingLeft: '4px',
            paddingRight: '4px',
            display: 'flex',
            flexGrow: 1,
        },
    };
});

const BalanceByDayOfWeek = ({
    selectedBalanceView,
    handleDayOfWeekClick,
}: {
    selectedBalanceView: DayOfWeek | 'daily';
    handleDayOfWeekClick: (dayOfWeek: DayOfWeek) => void;
}): React.JSX.Element => {
    const match = useMatch(privateRoutes.areaBalance);

    const areaBalanceDetailsApiStatus = useAppSelector((state) => { return state.areaBalance.areaBalanceDetailsApiStatus; });
    const areaBalanceDetails = useAppSelector((state) => { return state.areaBalance.areaBalanceDetails; });

    const areaName = match?.params.name;

    const determineBalanceLevelClass = (balance: number | null): string | undefined => {
        let className: string | undefined;

        if (balance !== null) {
            const absoluteBalance = Math.abs(balance);

            if (absoluteBalance <= 2) {
                className = classes.goodBalance;
            } else if (absoluteBalance <= 5) {
                className = classes.okayBalance;
            } else {
                className = classes.poorBalance;
            }
        }

        return className;
    };

    const renderDayOfWeekBalances = (): React.JSX.Element[] => {
        if (areaBalanceDetailsApiStatus === ApiStatus.Loading) {
            return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((dayOfWeek) => {
                return (
                    <Grid key={dayOfWeek} item className={classes.dayOfWeekGridItem}>
                        <Card>
                            <CardContent className={classes.dayOfWeekCardContent}>
                                <Typography variant='h6'>{dayOfWeek}</Typography>
                                <Typography variant='h5' className={classes.dayOfWeekBalance}><Skeleton width='100%' /></Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            });
        }

        if (areaBalanceDetailsApiStatus === ApiStatus.Failure) {
            return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((dayOfWeek) => {
                return (
                    <Grid key={dayOfWeek} item className={classes.dayOfWeekGridItem}>
                        <Card>
                            <CardContent className={classes.dayOfWeekCardContent}>
                                <Typography variant='h6'>{dayOfWeek}</Typography>
                                <Typography variant='h5' className={classes.dayOfWeekBalance}>{ValueReferences.NA}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            });
        }

        return areaBalanceDetails.day_of_week_balance.map((dayOfWeekBalance) => {
            const balanceLevelClass = determineBalanceLevelClass(dayOfWeekBalance.day_freight_order_average_balance);

            return (
                <Grid key={dayOfWeekBalance.day_of_week_name} item className={classes.dayOfWeekGridItem}>
                    <StyledTooltip
                        title={
                            <Grid container direction='column' spacing={1}>
                                <Grid item xs={12}>
                                    <Typography variant='body2' className={classes.totalOrders}>
                                        <span>Orders into</span>
                                        <span className={classes.areaNameLabel}>{areaName}</span>
                                        <span>{formatNumber(dayOfWeekBalance.day_inbound_freight_order_total_count, 0, 0)}</span>
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant='body2' className={classes.totalOrders}>
                                        <span>Orders out of</span>
                                        <span className={classes.areaNameLabel}>{areaName}</span>
                                        <span>{formatNumber(dayOfWeekBalance.day_outbound_freight_order_total_count, 0, 0)}</span>
                                    </Typography>
                                </Grid>
                            </Grid>
                        }
                    >
                        <Card className={selectedBalanceView === dayOfWeekBalance.day_of_week_name ? classes.selectedCard : undefined}>
                            <CardActionArea
                                onClick={(): void => {
                                    handleDayOfWeekClick(dayOfWeekBalance.day_of_week_name);
                                }}
                            >
                                <CardContent className={classes.dayOfWeekCardContent}>
                                    <Typography variant='h6'>{dayOfWeekBalance.day_of_week_name.substring(0, 3).toUpperCase()}</Typography>
                                    <Typography variant='h5' className={`${classes.dayOfWeekBalance} ${balanceLevelClass}`}>
                                        {
                                            dayOfWeekBalance.day_freight_order_average_balance !== null ?
                                                formatBalance(dayOfWeekBalance.day_freight_order_average_balance) :
                                                '--'
                                        }
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </StyledTooltip>
                </Grid>
            );
        });
    };

    return (
        <StyledGrid
            container
            direction='column'
            justifyContent='flex-end'
        >
            <Grid item xs={12}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Typography variant='h4'>
                            Balance by Day of Week
                        </Typography>
                    </Grid>
                    {renderDayOfWeekBalances()}
                </Grid>
            </Grid>
        </StyledGrid>
    );
};

export default BalanceByDayOfWeek;
